import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, X, Download, DollarSign, CreditCard, Banknote, CheckCircle, XCircle } from 'lucide-react';
import { payrollPaymentService, payslipService } from '../api/mockRepo';
import { exportToCSV } from '../../../lib/csv';
import type { PayrollPayment, Payslip, PayrollPaymentFormData } from '../types';
import toast from 'react-hot-toast';
import { checkVouchersExist } from '../../../services/payrollAccountingService';

const paymentSchema = z.object({
  payslip_id: z.string().min(1, 'Payslip is required'),
  amount: z.number().min(0.01, 'Amount must be greater than 0'),
  payment_date: z.string().min(1, 'Payment date is required'),
  payment_method: z.enum(['cash', 'bank_transfer', 'cheque']),
  reference_number: z.string().optional(),
  bank_account: z.string().optional(),
  notes: z.string().optional()
});

export default function PaymentsListPage() {
  const [payments, setPayments] = useState<PayrollPayment[]>([]);
  const [payslips, setPayslips] = useState<Payslip[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [voucherStatus, setVoucherStatus] = useState<Record<string, boolean>>({});

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors }
  } = useForm<PayrollPaymentFormData>({
    resolver: zodResolver(paymentSchema)
  });

  const paymentMethod = watch('payment_method');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [paymentsData, payslipsData] = await Promise.all([
        payrollPaymentService.getAll(),
        payslipService.getAll()
      ]);
      setPayments(paymentsData);
      setPayslips(payslipsData.filter(p => p.status === 'approved'));
      
      const voucherStatusMap: Record<string, boolean> = {};
      for (const payment of paymentsData) {
        try {
          const hasVouchers = await checkVouchersExist(payment.id);
          voucherStatusMap[payment.id] = hasVouchers;
        } catch (error) {
          console.error(`Error checking vouchers for payment ${payment.id}:`, error);
          voucherStatusMap[payment.id] = false;
        }
      }
      setVoucherStatus(voucherStatusMap);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: PayrollPaymentFormData) => {
    try {
      const created = await payrollPaymentService.create(data);
      setPayments(prev => [created, ...prev]);
      await loadData();
      toast.success('Payment recorded successfully. Accounting vouchers created.');
      setShowModal(false);
      reset();
    } catch (error: any) {
      toast.error(error.message || 'Failed to record payment');
    }
  };

  const handleExportCSV = () => {
    const enrichedPayments = payments.map(payment => {
      const payslip = payslips.find(p => p.id === payment.payslip_id);
      return {
        ...payment,
        employee_name: payslip?.employee_name || 'Unknown',
        employee_code: payslip?.employee_code || 'N/A',
        department: payslip?.department || 'N/A'
      };
    });

    exportToCSV(
      enrichedPayments,
      [
        'Payment ID', 'Employee Code', 'Employee Name', 'Department',
        'Amount', 'Payment Date', 'Payment Method', 'Reference Number',
        'Bank Account', 'Status', 'Notes'
      ],
      (payment) => [
        payment.id,
        payment.employee_code,
        payment.employee_name,
        payment.department,
        payment.amount.toString(),
        payment.payment_date,
        payment.payment_method,
        payment.reference_number || '',
        payment.bank_account || '',
        payment.status,
        payment.notes || ''
      ],
      `payroll-payments-${new Date().toISOString().split('T')[0]}.csv`
    );
  };

  const getPaymentMethodIcon = (method: PayrollPayment['payment_method']) => {
    switch (method) {
      case 'cash': return <Banknote className="w-4 h-4" />;
      case 'bank_transfer': return <CreditCard className="w-4 h-4" />;
      case 'cheque': return <DollarSign className="w-4 h-4" />;
      default: return <DollarSign className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status: PayrollPayment['status']) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEmployeeName = (payslipId: string) => {
    const payslip = payslips.find(p => p.id === payslipId);
    return payslip ? payslip.employee_name : 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Payroll Payments</h1>
          <p className="text-sm text-neutral-600">Record and track salary payments</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleExportCSV}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </button>
          <button
            onClick={() => {
              reset();
              setShowModal(true);
            }}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Record Payment
          </button>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <DollarSign className="w-8 h-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Total Payments</p>
              <p className="text-2xl font-bold text-neutral-900">{payments.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <DollarSign className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Total Amount</p>
              <p className="text-2xl font-bold text-neutral-900">
                ${payments.reduce((sum, p) => sum + p.amount, 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <CreditCard className="w-8 h-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Bank Transfers</p>
              <p className="text-2xl font-bold text-neutral-900">
                {payments.filter(p => p.payment_method === 'bank_transfer').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <Banknote className="w-8 h-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Cash Payments</p>
              <p className="text-2xl font-bold text-neutral-900">
                {payments.filter(p => p.payment_method === 'cash').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-lg border">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reference
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Accounting
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {payments.map((payment) => (
                <tr key={payment.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {getEmployeeName(payment.payslip_id)}
                    </div>
                    <div className="text-sm text-gray-500">
                      ID: {payment.staff_id}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      ${payment.amount.toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {payment.payment_date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {getPaymentMethodIcon(payment.payment_method)}
                      <span className="ml-2 text-sm text-gray-900 capitalize">
                        {payment.payment_method.replace('_', ' ')}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {payment.reference_number || '-'}
                    </div>
                    {payment.bank_account && (
                      <div className="text-sm text-gray-500">
                        {payment.bank_account}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                      {payment.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {voucherStatus[payment.id] ? (
                      <div className="flex items-center text-green-600">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        <span className="text-xs">Vouchers Created</span>
                      </div>
                    ) : (
                      <div className="flex items-center text-gray-400">
                        <XCircle className="w-4 h-4 mr-1" />
                        <span className="text-xs">No Vouchers</span>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold">Record Payment</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payslip *
                </label>
                <select
                  {...register('payslip_id')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select payslip</option>
                  {payslips.map(payslip => (
                    <option key={payslip.id} value={payslip.id}>
                      {payslip.employee_name} - ${payslip.net_salary.toLocaleString()}
                    </option>
                  ))}
                </select>
                {errors.payslip_id && (
                  <p className="text-red-500 text-sm mt-1">{errors.payslip_id.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount *
                </label>
                <input
                  type="number"
                  step="0.01"
                  {...register('amount', { valueAsNumber: true })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.amount && (
                  <p className="text-red-500 text-sm mt-1">{errors.amount.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Date *
                </label>
                <input
                  type="date"
                  {...register('payment_date')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.payment_date && (
                  <p className="text-red-500 text-sm mt-1">{errors.payment_date.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Method *
                </label>
                <select
                  {...register('payment_method')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select method</option>
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                </select>
                {errors.payment_method && (
                  <p className="text-red-500 text-sm mt-1">{errors.payment_method.message}</p>
                )}
              </div>

              {(paymentMethod === 'bank_transfer' || paymentMethod === 'cheque') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reference Number
                    </label>
                    <input
                      type="text"
                      {...register('reference_number')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Transaction/Cheque number"
                    />
                  </div>

                  {paymentMethod === 'bank_transfer' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Bank Account
                      </label>
                      <input
                        type="text"
                        {...register('bank_account')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Account number or details"
                      />
                    </div>
                  )}
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  {...register('notes')}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Optional payment notes"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Record Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {payments.length === 0 && (
        <div className="text-center py-12">
          <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No payments recorded</h3>
          <p className="mt-1 text-sm text-gray-500">
            Start by recording salary payments for approved payslips.
          </p>
        </div>
      )}
    </div>
  );
}
